import { AriaAttributes, FC, KeyboardEvent, ReactNode, useEffect, useState } from 'react';

import { PositionStrategy } from 'react-tooltip';
import ResizeObserver from 'resize-observer-polyfill';

import { KeyboardKey } from '@telialt/common-constants/src/keyboard-keys';
import HtmlContent from '@telialt/common-html-content/src';
import { useComponentId } from '@telialt/utils/src/hooks';

import { TooltipContent, TooltipContentWrapper, TooltipTrigger } from './Tooltip.styles';

type Props = {
    id?: string;
    children: ReactNode;
    content: ReactNode;
    className?: string;
    positionStrategy?: PositionStrategy;
    shouldOpenOnHover?: boolean;
    hidden?: boolean;
    isContentHidden?: boolean;
    isInitialOpen?: boolean;
    contentWidth?: string;
    dataTest?: string;
    inverted?: boolean;
    forceOpen?: boolean;
    anchorSelect?: string;
    ariaLabel?: string;
    tabIndex?: number;
} & AriaAttributes;

export const Tooltip: FC<Props> = ({
    children,
    content,
    className,
    positionStrategy,
    shouldOpenOnHover,
    hidden,
    id,
    dataTest,
    isInitialOpen = false,
    contentWidth,
    isContentHidden,
    inverted,
    forceOpen,
    anchorSelect,
    ariaLabel,
    tabIndex = 0,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(isInitialOpen);
    const generatedId = `tooltip-${useComponentId().replace(/[:, #]/g, '')}`;
    const tooltipId = id || generatedId;

    useEffect(() => {
        if (isInitialOpen) {
            setIsOpen(true);
        }
    }, [isInitialOpen]);

    if (typeof window !== 'undefined') {
        window.ResizeObserver = ResizeObserver;
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === KeyboardKey.Enter || event.key === KeyboardKey.Space) {
            setIsOpen(!isOpen);
        } else if (event.key === KeyboardKey.Escape) {
            setIsOpen(false);
        }
    };

    return (
        <>
            <TooltipTrigger
                {...rest}
                id={tooltipId}
                onClick={() => setIsOpen(forceOpen || !isOpen)}
                aria-label={ariaLabel}
                className={className}
                hidden={hidden}
                data-tooltip-position-strategy={positionStrategy}
                data-test={dataTest}
                $isPointer={!isContentHidden}
                tabIndex={tabIndex}
                role="button"
                aria-pressed={isOpen}
                onKeyDown={handleKeyDown}
                aria-describedby={`${tooltipId}-content`}
            >
                {children}
            </TooltipTrigger>
            <TooltipContentWrapper role="status">
                <TooltipContent
                    id={`${tooltipId}-content`}
                    role="tooltip"
                    $inverted={inverted}
                    hidden={isContentHidden}
                    anchorSelect={anchorSelect || `#${tooltipId}`}
                    clickable={true}
                    openOnClick={!shouldOpenOnHover}
                    opacity={1}
                    disableStyleInjection={true}
                    isOpen={isOpen}
                    $contentWidth={contentWidth}
                    // the logic is a bit not straight forward, but seems to be the only way we can have both close on click outside and second click on trigger
                    setIsOpen={(open: boolean) => setIsOpen(forceOpen || (open ? !isOpen : false))}
                >
                    {typeof content === 'string' ? <HtmlContent content={content} /> : content}
                </TooltipContent>
            </TooltipContentWrapper>
        </>
    );
};
